import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { t } from '../../services/i18nService';
import utilService from '../../services/UtilService';
import responseService from '../../services/ResponseService';
import styles from './QuestionDisplay.scss';
import resultReader from '../../store/readHelpers/ResultReader';
import questionDisplayFooterSelector from '../../selectors/question/QuestionDisplayFooterSelector';
import questionReader from '../../store/readHelpers/QuestionReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import themeServices from '../../themes/ThemeServices';
import essayRestoreService, { essayRestoreKey }
    from '../../services/question/interactions/extendedTextInteraction/EssayRestoreService';

class QuestionDisplayFooter extends React.PureComponent {
    showCorrectResponse = () => {
        const { question, correctResponsesForQuestion } = this.props;
        if (questionReader.isGapMatchQuestionWithMultipleInteractions(question)) {
            question.responseDeclarations.forEach((responseDeclaration) => {
                const responseIdentifier = responseDeclaration.identifier;
                responseService.handleUserResponse(question.contentRevisionId, responseIdentifier, correctResponsesForQuestion[responseIdentifier]);
            });
        } else {
            const responseIdentifier = question.responseDeclarations[0].identifier;
            responseService.handleUserResponse(question.contentRevisionId, responseIdentifier, correctResponsesForQuestion[responseIdentifier]);
        }
        responseService.updateItemResult(question.contentRevisionId);
        responseService.saveResponseAndFetchResponse(question.contentRevisionId);
    }

    getFeedbackText(outcomeVariables, question, questionResult) {
        if (!outcomeVariables) {
            return '';
        }

        let feedbackText = '';
        if (outcomeVariables.FEEDBACK) {
            feedbackText = t(`result.${outcomeVariables.FEEDBACK.value}`);
        }

        const scoreFromResultOrOutcome = resultReader.getScoreFromResultOrOutcome(outcomeVariables, questionResult);
        if (
            !isNaN(scoreFromResultOrOutcome)
            && !utilService.isQuestionTypeManuallyScored(question.objectType)
            && !utilService.isQuestionTypeNotScorable(question.objectType)
        ) {
            const scoreText = t('result.scoreFooter', {
                score: scoreFromResultOrOutcome,
                maxScore: question.maxScore,
            });
            feedbackText = `${feedbackText}. ${scoreText} `;
        } else {
            feedbackText = `${feedbackText}. `;
        }

        return feedbackText;
    }

    getMaxScoreText(question, showInBrackets) {
        const text = showInBrackets
            ? `[${question.maxScore}]`
            : t('question.maximumMarks', { maxMarks: question.maxScore });

        return `${text} `;
    }

    /** A deliberate reset: discard the essay backups so no restore is offered. */
    noteEssayResetIsDeliberate = () => {
        const { question } = this.props;
        (question.responseModels || []).forEach((responseModel) => {
            const key = essayRestoreKey(question.contentRevisionId, responseModel.identifier);
            essayRestoreService.suppressDetection(key);
            essayRestoreService.discardBackup(key);
        });
    };

    resetResponsesForCurrentQuestion = () => {
        const { question } = this.props;
        this.noteEssayResetIsDeliberate();
        responseService.resetResponsesForQuestion(question.contentRevisionId);
    };

    resetResponsesForCurrentQuestionInPreview = () => {
        const { question } = this.props;
        console.log(`reset in preview button clicked, contentRevisionId - ${question.contentRevisionId}`);
        this.noteEssayResetIsDeliberate();
        responseService.resetResponsesForQuestion(question.contentRevisionId, true);
    };

    checkResponseForCurrentQuestion = () => {
        const { question } = this.props;
        responseService.updateItemResult(question.contentRevisionId);
        responseService.saveResponseAndFetchResponse(question.contentRevisionId);
    };

    handleTryAgainClick = () => {
        const { question } = this.props;
        console.log(`try again button clicked, contentRevisionId - ${question.contentRevisionId}`);
        this.resetResponsesForCurrentQuestion();
    };

    handleResetClick = () => {
        const { question } = this.props;
        console.log(`reset button clicked, contentRevisionId - ${question.contentRevisionId}`);
        this.resetResponsesForCurrentQuestion();
    };

    render() {
        const {
            isReadOnly,
            outcomeVariables,
            question,
            questionResult,
            showCheckAnswer,
            showInfoBox,
            showMaxScore,
            showPopulateWithCorrectAnswer,
            showQuestionOutcome,
            showResetButton,
            showResetInPreviewButton,
            showTheLine,
            ui,
        } = this.props;

        const footerClasses = [styles.footer];

        const isPaperBasedAndCEQ = themeServices.isCeq() && sessionReader.isPrintStylePaperBased();
        const shouldShowFooter = showCheckAnswer || showInfoBox || showQuestionOutcome || showResetButton || (showMaxScore && !showCheckAnswer);
        if (!shouldShowFooter && isPaperBasedAndCEQ) {
            footerClasses.push(styles.removeEmptyFooterSpacing);
        }

        if (showTheLine) {
            footerClasses.push('checkanswer-view');
        }

        if (showInfoBox) {
            return (<div className={styles.infobox}>
                <span className="text">{t('question.wrongAnswer')}</span>
                <button
                    className={styles.infoBtn}
                    type="button"
                    onClick={this.handleTryAgainClick}
                    tabIndex={0}
                >
                    <i className="fa fa-repeat" aria-hidden="true" />
                    <span className={styles.btnText}>{t('question.tryAgain')}</span>
                </button>
            </div>);
        }

        return (<div className={footerClasses.join(' ')}>
            { showResetButton &&
                <div className={styles.resetButton}>
                    <button
                        onClick={this.handleResetClick}
                        disabled={isReadOnly}
                        tabIndex={0}
                    >
                        { t('question.reset') }
                    </button>
                </div>
            }

            { showCheckAnswer &&
                <div>
                    { showMaxScore && this.getMaxScoreText(question, ui.variables.question__showMarksInBrackets) }
                    { showPopulateWithCorrectAnswer &&
                        <button
                            className={styles.showCorrectAnswers}
                            onClick={this.showCorrectResponse}
                            tabIndex={0}
                        >
                            {t('question.showCorrectAnswers')}
                        </button>
                    }

                    <button
                        className={styles.checkAnswerButton}
                        onClick={this.checkResponseForCurrentQuestion}
                        tabIndex={0}
                    >
                        {t('question.checkAnswerInPreview')}
                    </button>
                </div>
            }

            { showQuestionOutcome &&
                <div>
                    <div className={styles.questionOutcomeText}>
                        { this.getFeedbackText(outcomeVariables, question, questionResult) }

                        { showResetInPreviewButton &&
                            <button
                                className={styles.resetInPreviewButton}
                                onClick={this.resetResponsesForCurrentQuestionInPreview}
                            >
                                {t('question.resetInPreview')}
                            </button>
                        }
                    </div>
                </div>
            }

            { showMaxScore && !showCheckAnswer &&
                <div className={styles.maxScore}>
                    { this.getMaxScoreText(question, ui.variables.question__showMarksInBrackets) }
                </div>
            }
        </div>);
    }
}

QuestionDisplayFooter.propTypes = {
    correctResponsesForQuestion: PropTypes.object,
    isReadOnly: PropTypes.bool,
    outcomeVariables: PropTypes.object,
    question: PropTypes.object,
    questionResult: PropTypes.object,
    showCheckAnswer: PropTypes.bool.isRequired,
    showInfoBox: PropTypes.bool.isRequired,
    showMaxScore: PropTypes.bool.isRequired,
    showPopulateWithCorrectAnswer: PropTypes.bool.isRequired,
    showQuestionOutcome: PropTypes.bool.isRequired,
    showResetButton: PropTypes.bool.isRequired,
    showResetInPreviewButton: PropTypes.bool.isRequired,
    showTheLine: PropTypes.bool.isRequired,
    ui: PropTypes.object,
};

const mapStateToProps = (state, ownProps) => questionDisplayFooterSelector.selectProps(state, ownProps);
export default connect(mapStateToProps)(QuestionDisplayFooter);
